// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

// Owns one verified reader edition, selection admission, processing and output resource lifetimes.
import type { PdfjsViewer, PdfjsViewerPrintOutput } from "@unilarva/pdfjs-viewer";
import type { Translator } from "@unilarva/translator";
import { Logger } from "../../shared/src/generic/logger";
import { MultikeyController, type MultikeyOccurrence } from "./multikey";
import { MultikeyBackend } from "./multikey-backend";
import { mountMultikeyControls } from "./multikey-controls";
import {
  cachedMultikeyWorker,
  loadMultikeyIndex,
  loadMultikeyRange,
  verifyMultikeyCanonical,
  prefetchMultikeyRanges,
  checkMultikeyOfflineReady,
  claimMultikeySourceInventory,
  type MultikeyResourceOptions,
} from "./multikey-resources";
import { multikeyRangeForPage } from "./multikey-index";
import { MultikeyArtifactCache } from "./multikey-artifact-cache";
import { MultikeyWorkerClient } from "./multikey-worker-client";
import { captureMultikeyExportSnapshot } from "./multikey-export-snapshot";
import { MultikeyBookExporter } from "./multikey-export";
import { mountMultikeyOutputControls } from "./multikey-output-controls";

/** One canonical reader's private selection, processor, controls and cache lifetime. */
export interface MultikeySession {
  readonly controller: MultikeyController;
  readonly backend: MultikeyBackend;
  /** Last cache-only verification; rechecked on connectivity/visibility changes and source failures. */
  readonly offlineKeyChangeReady: boolean;
  /** Restoration, selection publication or visible-page readiness is not yet settled. */
  readonly outputPending: boolean;
  /** Actual committed changes in this loaded edition, not saved preferences. */
  readonly hasOutputChanges: boolean;
  /** Synchronously captures committed keys. Resolution is lazy; release cancels its own work. */
  admitOutput(signal: AbortSignal): PdfjsViewerPrintOutput | null;
  dispose(): void;
}

const sessionResources = {
  loadMultikeyIndex,
  loadMultikeyRange,
  verifyMultikeyCanonical,
  prefetchMultikeyRanges,
  checkMultikeyOfflineReady,
  claimMultikeySourceInventory,
};

/** Opens processing after the ordinary reader is already usable; never reloads it with companion bytes. */
export async function openMultikeySession(options: {
  viewer: PdfjsViewer;
  root: HTMLElement;
  indexUrl: string;
  canonicalUrl: string;
  bookId: string;
  translator: Translator;
  /** Resolves consumer-owned song labels for the controls' accessible titles. */
  songTitle?: (occurrence: MultikeyOccurrence) => Promise<string>;
  signal: AbortSignal;
  isActive: () => boolean;
  workerUrl?: string | URL;
  fetcher?: typeof fetch;
  /** Narrow test/host resource port; production uses the edition-checked CacheStorage path. */
  resources?: Partial<typeof sessionResources>;
}): Promise<MultikeySession | null> {
  const { viewer, root, signal } = options;
  const resources = { ...sessionResources, ...options.resources };
  const window = root.ownerDocument.defaultView;
  const token = viewer.documentId;
  if (!window || token === null || signal.aborted || viewer.state.status !== "ready")
    throw new DOMException("Reader is no longer available", "AbortError");
  if (
    new URL(options.canonicalUrl, window.location.href).href ===
    new URL(options.indexUrl, window.location.href).href
  )
    throw new Error("Ordinary PDF and multikey index must be independently configured");
  const abort = new AbortController();
  const lifetime = AbortSignal.any([signal, abort.signal]);
  let client: MultikeyWorkerClient | undefined;
  let backend: MultikeyBackend | undefined;
  let controller: MultikeyController | undefined;
  let disposeControls: (() => void) | undefined;
  let workerAsset: Awaited<ReturnType<typeof cachedMultikeyWorker>> = null;
  let exportAsset: Awaited<ReturnType<typeof cachedMultikeyWorker>> = null;
  let exportSetup: Promise<MultikeyBookExporter> | undefined;
  let exporter: MultikeyBookExporter | undefined;
  let outputStatusOwner: AbortController | undefined;
  let disposeOutputControls: (() => void) | undefined;
  let artifacts: MultikeyArtifactCache | undefined;
  let sourceStatus: HTMLElement | undefined;
  let offlineKeyChangeReady = false;
  let disposeSourceChecks: (() => void) | undefined;
  let disposeSourceTranslation: (() => void) | undefined;
  let disposeSourceStatus: (() => void) | undefined;
  let occurrences: readonly MultikeyOccurrence[] = [];
  let disposed = false;
  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    abort.abort();
    root.removeEventListener("pdf:statechange", documentChanged);
    signal.removeEventListener("abort", dispose);
    disposeControls?.();
    disposeOutputControls?.();
    exporter?.dispose();
    controller?.dispose();
    backend?.dispose();
    client?.dispose();
    workerAsset?.release();
    exportAsset?.release();
    artifacts?.close();
    sourceStatus?.remove();
    disposeSourceChecks?.();
    disposeSourceTranslation?.();
    disposeSourceStatus?.();
    delete root.dataset.sbMultikeySources;
    delete root.dataset.sbMultikeyOfflineReady;
  };
  const current = (): boolean => !lifetime.aborted && viewer.documentId === token;
  const documentChanged = (): void => {
    if (!current()) dispose();
  };
  root.addEventListener("pdf:statechange", documentChanged);
  signal.addEventListener("abort", dispose, { once: true });
  try {
    let cacheStorage: CacheStorage | undefined;
    try {
      cacheStorage = window.caches;
    } catch {
      /* Storage denial does not block online reading. */
    }
    // HTTP localhost is trustworthy; a LAN HTTP origin is not. Online processing still works.
    const offlineUnavailable =
      window.isSecureContext === false
        ? "pdf.transpose-offline-https-required"
        : typeof cacheStorage?.open !== "function"
          ? "pdf.transpose-offline-storage-unavailable"
          : undefined;
    const offline = window.navigator.onLine === false;
    const workerUrl = options.workerUrl ?? new URL("./multikey-worker.js", import.meta.url);
    const cachedWorker = async (): Promise<typeof workerAsset> => {
      const url = new URL(workerUrl, window.location.href);
      if (url.origin !== window.location.origin) return null;
      const asset = await cachedMultikeyWorker(
        url.href,
        lifetime,
        cacheStorage,
        data =>
          window.URL.createObjectURL(
            new window.Blob([new Uint8Array(data)], { type: "text/javascript" }),
          ),
        url => window.URL.revokeObjectURL(url),
      );
      if (!current()) {
        asset?.release();
        throw new DOMException("Reader changed", "AbortError");
      }
      return asset;
    };
    const resourceOptions: MultikeyResourceOptions = {
      offline,
      cacheStorage,
      fetcher: options.fetcher,
      canonicalUrl: new URL(options.canonicalUrl, window.location.href).href,
      deferInventoryClaim: true,
    };
    const {
      index,
      identity: indexIdentity,
      inventory,
      previousIndex,
      deferredInventoryClaim,
    } = await resources.loadMultikeyIndex(
      new URL(options.indexUrl, window.location.href).href,
      lifetime,
      resourceOptions,
    );
    resourceOptions.inventory = inventory;
    // Exact loaded reader bytes, not a potentially newer response stored under the same URL.
    // This temporary viewer-owned snapshot is transferred to the lightweight hash worker, not retained.
    await (async () => {
      const canonical = await viewer.getDocumentData({ document: "original" });
      if (!canonical.ok) throw new Error(`Ordinary PDF bytes unavailable: ${canonical.reason}`);
      await resources.verifyMultikeyCanonical(canonical.data, index, lifetime, resourceOptions);
    })();
    if (index.pageCount !== viewer.state.pageCount)
      throw new Error("Multikey canonical page count mismatch");
    if (!current()) throw new DOMException("Reader changed", "AbortError");
    if (deferredInventoryClaim)
      resourceOptions.inventory = await resources.claimMultikeySourceInventory(
        index,
        previousIndex,
        lifetime,
        resourceOptions,
      );
    occurrences = index.occurrences;
    if (occurrences.length === 0) {
      dispose();
      return null;
    }
    artifacts = new MultikeyArtifactCache();
    // A validated model is an optimization/diagnostic record, never a replacement for current identity checks.
    void artifacts
      .putMetadata(indexIdentity, { index, canonicalUrl: options.canonicalUrl })
      .catch(() => {});
    const statusButton = root.ownerDocument.createElement("button");
    sourceStatus = statusButton;
    statusButton.type = "button";
    sourceStatus.className = "sb-multikey-source-status";
    sourceStatus.hidden = true;
    const statusText = root.ownerDocument.createElement("span");
    statusText.setAttribute("role", "status");
    sourceStatus.append(statusText);
    (root.querySelector(".pdf-viewer-body") ?? root).append(sourceStatus);
    let dismissed = false;
    let dismissTimer: number | undefined;
    const clearDismissTimer = (): void => {
      if (dismissTimer !== undefined) window.clearTimeout(dismissTimer);
      dismissTimer = undefined;
    };
    const dismissStatus = (): void => {
      dismissed = true;
      statusButton.hidden = true;
      clearDismissTimer();
    };
    // Native button activation covers pointer, Enter and Space without custom key handling.
    statusButton.addEventListener("click", dismissStatus);
    disposeSourceStatus = () => {
      clearDismissTimer();
      statusButton.removeEventListener("click", dismissStatus);
    };
    let lastProgress: { completed: number; total: number; state: string } | undefined;
    let sourceDownloadStarted = false;
    const translateSourceStatus = (): void => {
      if (!current()) return;
      const close = options.translator.translateKey("button.close");
      statusButton.title = close;
      statusButton.setAttribute("aria-label", close);
      if (!lastProgress) return;
      const text = options.translator.translateKey(
        offlineUnavailable ??
          (offlineKeyChangeReady
            ? "pdf.transpose-offline-ready"
            : lastProgress.state === "ready"
              ? "pdf.transpose-offline-partial"
              : lastProgress.state === "partial"
                ? "pdf.transpose-sources-partial"
                : lastProgress.state === "downloading" || sourceDownloadStarted
                  ? "pdf.transpose-sources-downloading"
                  : "pdf.transpose-sources-preparing"),
        { values: { completed: lastProgress.completed, total: lastProgress.total } },
      );
      // Avoid re-announcing unchanged progress during connectivity checks.
      if (statusText.textContent !== text) statusText.textContent = text;
      statusButton.hidden = dismissed;
      if (!offlineKeyChangeReady) clearDismissTimer();
      else if (!dismissed && dismissTimer === undefined)
        dismissTimer = window.setTimeout(dismissStatus, 5_000);
    };
    const sourceProgress = (progress: {
      completed: number;
      total: number;
      state: string;
    }): void => {
      if (!current() || !sourceStatus) return;
      // Downloading includes the rest of this pass's cache verification, without another notice.
      if (progress.state === "downloading") sourceDownloadStarted = true;
      lastProgress = progress;
      root.dataset.sbMultikeySources = progress.state;
      translateSourceStatus();
      root.dispatchEvent(new window.CustomEvent("sb:multikeysourceschange", { detail: progress }));
    };
    // Restoration and output can fetch a range before the serialized prefetch reaches it.
    resourceOptions.onRangeDownload = active => {
      if (!current()) return;
      if (active) {
        offlineKeyChangeReady = false;
        root.dataset.sbMultikeyOfflineReady = "false";
      }
      sourceProgress({
        completed: lastProgress?.completed ?? 0,
        total: index.ranges.length,
        state: active ? "downloading" : "preparing",
      });
    };
    let sourceCheck: Promise<void> | undefined;
    const unusableRanges = new Set<string>();
    const checkOffline = (): Promise<void> => {
      resourceOptions.offline = window.navigator.onLine === false;
      if (offlineUnavailable) return Promise.resolve();
      // Coalesce visibility/network events: stale-result guards alone would leave full-book
      // checksum scans running concurrently even after their results became obsolete.
      if (sourceCheck) return sourceCheck;
      sourceCheck = (async () => {
        offlineKeyChangeReady = false;
        root.dataset.sbMultikeyOfflineReady = "false";
        const cached = await resources
          .checkMultikeyOfflineReady(index, options.canonicalUrl, lifetime, resourceOptions)
          .catch(() => false);
        if (!current() || !sourceStatus) return;
        const ready = cached && unusableRanges.size === 0;
        offlineKeyChangeReady = ready;
        root.dataset.sbMultikeyOfflineReady = String(ready);
        translateSourceStatus();
        root.dispatchEvent(
          new window.CustomEvent("sb:multikeyofflinereadychange", { detail: { ready } }),
        );
      })().finally(() => {
        sourceCheck = undefined;
      });
      return sourceCheck;
    };
    let prefetchInFlight: Promise<void> | undefined;
    let prefetchAgain = false;
    const startPrefetch = (): void => {
      if (!current()) return;
      resourceOptions.offline = window.navigator.onLine === false;
      if (offlineUnavailable) {
        root.dataset.sbMultikeyOfflineReady = "false";
        sourceProgress({ completed: 0, total: index.ranges.length, state: "unavailable" });
        return;
      }
      if (prefetchInFlight) {
        prefetchAgain = true;
        return;
      }
      sourceDownloadStarted = false;
      offlineKeyChangeReady = false;
      root.dataset.sbMultikeyOfflineReady = "false";
      prefetchInFlight = resources
        .prefetchMultikeyRanges(index, lifetime, sourceProgress, resourceOptions)
        .catch(error => {
          if (current()) Logger.warn("Some key-change sources are not available offline", error);
        })
        .then(() => {
          if (current()) return checkOffline();
        })
        .finally(() => {
          prefetchInFlight = undefined;
          if (prefetchAgain && current() && window.navigator.onLine !== false) {
            prefetchAgain = false;
            startPrefetch();
          }
        });
    };
    const refreshSources = (event: Event): void => {
      if (current()) {
        if (event.type === "online") startPrefetch();
        else if (root.ownerDocument.visibilityState !== "hidden")
          void checkOffline().then(() => {
            if (
              current() &&
              !offlineKeyChangeReady &&
              window.navigator.onLine !== false &&
              !prefetchInFlight
            )
              startPrefetch();
          });
      }
    };
    window.addEventListener("online", refreshSources);
    window.addEventListener("offline", refreshSources);
    window.addEventListener("pageshow", refreshSources);
    root.ownerDocument.addEventListener("visibilitychange", refreshSources);
    disposeSourceTranslation = options.translator.subscribe(translateSourceStatus);
    translateSourceStatus();
    disposeSourceChecks = () => {
      window.removeEventListener("online", refreshSources);
      window.removeEventListener("offline", refreshSources);
      window.removeEventListener("pageshow", refreshSources);
      root.ownerDocument.removeEventListener("visibilitychange", refreshSources);
    };
    // Ordinary loading has completed. Sources, not eager per-key conversions, are downloaded serially.
    startPrefetch();
    const visiblePages = (): readonly number[] => {
      if (!current() || !options.isActive()) return [];
      const state = viewer.state;
      const pages = new Set<number>([state.currentPage]);
      const first =
        state.effectivePageLayout === "double"
          ? state.currentPage % 2 === 0
            ? state.currentPage - 1
            : state.currentPage
          : state.effectivePageLayout === "book" && state.currentPage > 1
            ? state.currentPage % 2 === 0
              ? state.currentPage
              : state.currentPage - 1
            : null;
      if (first !== null) {
        pages.add(first);
        pages.add(first + 1);
      }
      // Only app-owned overlay elements are inspected; no private viewer canvases or surfaces.
      const viewport =
        root.querySelector<HTMLElement>(".pdf-container")?.getBoundingClientRect() ??
        root.getBoundingClientRect();
      for (const element of root.querySelectorAll<HTMLElement>("[data-sb-multikey-page]")) {
        const bounds = element.getBoundingClientRect();
        if (
          bounds.width > 0 &&
          bounds.height > 0 &&
          bounds.bottom > viewport.top &&
          bounds.top < viewport.bottom &&
          bounds.right > viewport.left &&
          bounds.left < viewport.right
        )
          pages.add(Number(element.dataset.sbMultikeyPage));
      }
      return [...pages].filter(
        page => Number.isSafeInteger(page) && page > 0 && page <= state.pageCount,
      );
    };
    backend = new MultikeyBackend({
      viewer,
      rootEventTarget: root,
      client: {
        convert: async (request, jobOptions) => {
          const range = multikeyRangeForPage(index, request.pages[0]!);
          if (request.pages.some(page => multikeyRangeForPage(index, page) !== range))
            throw new Error("A processor job cannot span source ranges");
          const jobSignal = AbortSignal.any([
            lifetime,
            ...(jobOptions.signal ? [jobOptions.signal] : []),
          ]);
          let blob: Blob;
          try {
            resourceOptions.offline = window.navigator.onLine === false;
            blob = await resources.loadMultikeyRange(range, jobSignal, resourceOptions);
          } catch (error) {
            if (current()) void checkOffline();
            throw error;
          }
          if (!client) {
            // Cache-hit restoration and source prefetch never start the PDF processing worker.
            workerAsset = await cachedWorker();
            client = MultikeyWorkerClient.create({
              signal: lifetime,
              workerUrl: workerAsset?.url ?? workerUrl,
              workerFactory: (url, workerOptions) => new window.Worker(url, workerOptions),
            });
          }
          try {
            const result = await client.convertRange(
              {
                blob,
                identity: range.identity,
                byteLength: range.byteLength,
                firstPage: range.firstPage,
                geometry: index.geometry.slice(range.firstPage - 1, range.lastPage),
                occurrences: occurrences.filter(o =>
                  o.layerPageIndices.some(p => p + 1 >= range.firstPage && p + 1 <= range.lastPage),
                ),
              },
              request,
              { signal: jobSignal },
            );
            if (unusableRanges.delete(range.identity)) void checkOffline();
            return result;
          } catch (error) {
            if (current() && !jobSignal.aborted) {
              // Hash-complete source storage cannot attest a known unsupported PDF/worker.
              // Keep every key available for retry, but withdraw the whole-book readiness claim.
              unusableRanges.add(range.identity);
              offlineKeyChangeReady = false;
              root.dataset.sbMultikeyOfflineReady = "false";
              translateSourceStatus();
              root.dispatchEvent(
                new window.CustomEvent("sb:multikeyofflinereadychange", {
                  detail: { ready: false },
                }),
              );
            }
            throw error;
          }
        },
        dispose: () => client?.dispose(),
        get diagnostics() {
          return client?.diagnostics ?? { sourceBytes: 0, workerStarts: 0, conversions: 0 };
        },
      },
      index,
      indexIdentity,
      artifacts,
      occurrences,
      signal: lifetime,
      visiblePages,
    });
    const created = await MultikeyController.create({
      viewer,
      root,
      signal: lifetime,
      bookId: options.bookId,
      occurrences,
      backend,
    });
    if (!created.ok) throw new Error(`Multikey selections unavailable: ${created.reason}`);
    controller = created.controller;
    disposeControls = await mountMultikeyControls({
      viewer,
      controller,
      display: backend,
      translator: options.translator,
      songTitle: options.songTitle,
      root,
      signal: lifetime,
    });
    if (!current()) throw new DOMException("Reader changed", "AbortError");
    let restored = false;
    const session: MultikeySession = {
      controller,
      backend,
      get offlineKeyChangeReady() {
        return offlineKeyChangeReady;
      },
      get outputPending() {
        return !restored || controller!.pending() || backend!.pending();
      },
      get hasOutputChanges() {
        return occurrences.some(o => controller!.offset(o) !== 0);
      },
      admitOutput(operationSignal) {
        if (
          !current() ||
          !options.isActive() ||
          !viewer.state.active ||
          session.outputPending ||
          operationSignal.aborted
        )
          return null;
        let snapshot: ReturnType<typeof captureMultikeyExportSnapshot> | undefined =
          captureMultikeyExportSnapshot(
            index,
            indexIdentity,
            new Map(occurrences.map(o => [o, controller!.offset(o)])),
          );
        const owner = new AbortController();
        const jobSignal = AbortSignal.any([lifetime, operationSignal, owner.signal]);
        const outputStatus = (key: string | null): void => {
          if (key === "pdf.selected-output-preparing") outputStatusOwner = owner;
          if (outputStatusOwner !== owner || !current()) return;
          root.dispatchEvent(new window.CustomEvent("sb:multikeyoutputstatus", { detail: key }));
          if (key !== "pdf.selected-output-preparing") outputStatusOwner = undefined;
        };
        const activeChanged = (): void => {
          if (!options.isActive() || !viewer.state.active) owner.abort();
        };
        root.addEventListener("pdf:statechange", activeChanged);
        jobSignal.addEventListener(
          "abort",
          () => root.removeEventListener("pdf:statechange", activeChanged),
          { once: true },
        );
        return {
          resolveSource: async () => {
            const admitted = snapshot;
            if (!admitted || jobSignal.aborted || !current())
              throw new DOMException("Output cancelled", "AbortError");
            if (!admitted.changedPages.length) {
              const original = await viewer.getDocumentData({ document: "original" });
              if (!original.ok) throw new Error(`Original PDF unavailable: ${original.reason}`);
              if (jobSignal.aborted || !current())
                throw new DOMException("Output cancelled", "AbortError");
              return original.data;
            }
            outputStatus("pdf.selected-output-preparing");
            try {
              if (!exportSetup) {
                exportSetup = (async () => {
                  const url = new URL("./multikey-export-worker.js", import.meta.url);
                  exportAsset = await cachedMultikeyWorker(
                    url.href,
                    lifetime,
                    cacheStorage,
                    data =>
                      window.URL.createObjectURL(
                        new window.Blob([new Uint8Array(data)], { type: "text/javascript" }),
                      ),
                    url => window.URL.revokeObjectURL(url),
                  );
                  if (!current()) {
                    exportAsset?.release();
                    exportAsset = null;
                    throw new DOMException("Reader changed", "AbortError");
                  }
                  exporter = new MultikeyBookExporter({
                    viewer,
                    index,
                    indexIdentity,
                    signal: lifetime,
                    workerUrl: exportAsset?.url ?? url,
                    workerFactory: (url, workerOptions) => new window.Worker(url, workerOptions),
                    loadRange: (range, signal) => {
                      resourceOptions.offline = window.navigator.onLine === false;
                      return resources.loadMultikeyRange(range, signal, resourceOptions);
                    },
                  });
                  return exporter;
                })().catch(error => {
                  exportSetup = undefined;
                  throw error;
                });
              }
              // Script setup belongs to the session, but one cancelled admission must not
              // wait for a slow cache read (or cancel setup needed by another admission).
              const engine = await new Promise<MultikeyBookExporter>((resolve, reject) => {
                const abort = (): void =>
                  reject(new DOMException("Output cancelled", "AbortError"));
                jobSignal.addEventListener("abort", abort, { once: true });
                void exportSetup!.then(
                  engine => {
                    jobSignal.removeEventListener("abort", abort);
                    resolve(engine);
                  },
                  error => {
                    jobSignal.removeEventListener("abort", abort);
                    reject(error);
                  },
                );
                if (jobSignal.aborted) abort();
              });
              if (jobSignal.aborted || !current())
                throw new DOMException("Output cancelled", "AbortError");
              const result = await engine.export(admitted, { signal: jobSignal });
              if (!jobSignal.aborted && current()) outputStatus(null);
              return result;
            } catch (error) {
              if (!jobSignal.aborted && current()) outputStatus("pdf.selected-output-error");
              throw error;
            }
          },
          release: () => {
            snapshot = undefined;
            outputStatus(null);
            owner.abort();
          },
        };
      },
      dispose,
    };
    disposeOutputControls = mountMultikeyOutputControls({
      viewer,
      root,
      session,
      translator: options.translator,
      signal: lifetime,
      canonicalUrl: options.canonicalUrl,
    });
    void controller.ready.then(ok => {
      restored = true;
      root.dispatchEvent(new window.Event("sb:multikeyoutputchange"));
      if (!ok && current())
        Logger.warn("Saved keys could not be restored; the ordinary reader remains available");
    });
    return session;
  } catch (error) {
    dispose();
    // A control-mount failure can happen after a fast restoration committed. Restore
    // ordinary display without deleting the saved musical preferences.
    if (!signal.aborted && viewer.documentId === token && backend) {
      const pages = [
        ...new Set(occurrences.flatMap(record => record.layerPageIndices.map(page => page + 1))),
      ];
      if (pages.length)
        await viewer
          .setPageSourceOverrides(
            pages.map(page => ({ page, source: null })),
            token,
            { signal },
          )
          .catch(() => {});
    }
    throw error;
  }
}
